import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { NodeAvatar, Pill, EmptyState, showToast } from "../primitives";
import { useEntityType, useUpdateEntity } from "../../../lib/hooks/use-kuvio";
import { displayFieldFor } from "../../../lib/query-keys";
import { useRowDetail } from "../../hooks/use-row-detail";
import { useRowDetailPanel } from "./RowDetailContext";
import { FieldList } from "./FieldList";
import { StageStepper } from "./StageStepper";
import { RelatedSection } from "../scope/RelatedSection";
import { RowFilesSection } from "./RowFilesSection";
import { RowActivitySection } from "./RowActivitySection";
import type { AvailableTransition, EntityRecord, MutationError } from "../../../lib/types";

/**
 * Sprint 12 — the row as an operable surface. One `<RowDetail>` renders in two
 * modes: `panel` (480px slide-in, single column) and `page` (full route, two
 * columns at desktop widths, stacked below 900px). Header honors display roles
 * (title / subtitle / badge); the field list, stage stepper, owned files, and
 * back-references make up the body.
 */

interface RowDetailProps {
  entityTypeSlug: string;
  entityId: string;
  mode: "panel" | "page";
  onClose?: () => void;
  /** Panel mode: shown when the navigation stack has depth (FK drill-in). */
  showBack?: boolean;
  onBack?: () => void;
}

export function RowDetail({ entityTypeSlug, entityId, mode, onClose, showBack, onBack }: RowDetailProps) {
  const navigate = useNavigate();
  const { openPanel, pushPanel, closePanel } = useRowDetailPanel();
  const type = useEntityType(entityTypeSlug);
  const detail = useRowDetail(entityTypeSlug, entityId);
  const updateEntity = useUpdateEntity(type.data);
  const wide = useIsWide();

  const entity = detail.data?.entity as EntityRecord | undefined;

  function handleFieldSave(fieldName: string, value: unknown) {
    updateEntity.mutate(
      { id: entityId, fields: { [fieldName]: value } },
      { onError: (err) => showToast(mutationMessage(err), "error", true) },
    );
  }

  function handleTransition(newState: string) {
    const workflowField = type.data?.workflow?.field ?? "status";
    updateEntity.mutate(
      { id: entityId, fields: { [workflowField]: newState } },
      { onError: (err) => showToast(mutationMessage(err), "error", true) },
    );
  }

  // FK chips drill in: in the panel, push onto the stack so a "← Back" returns
  // to the prior record; on the full page, open the overlay panel fresh.
  const onOpenRef = mode === "panel" ? pushPanel : openPanel;

  // --- Loading / error / not-found ---
  if (detail.isLoading || type.isLoading) {
    return <Frame mode={mode}>{<DetailSkeleton />}</Frame>;
  }
  if (detail.error || !detail.data || !entity || !type.data) {
    return (
      <Frame mode={mode}>
        <div style={{ padding: 8 }}>
          <EmptyState>
            {detail.error ? "Couldn't load this record." : "Record not found."}
            <div style={{ marginTop: 8 }}>
              <button onClick={() => detail.refetch()} style={linkButtonStyle}>Retry</button>
            </div>
          </EmptyState>
        </div>
      </Frame>
    );
  }

  const def = type.data;
  const displayField = displayFieldFor(def);
  const roleValue = (role: string): string | undefined => {
    const f = def.fields.find((x) => x.display_role === role);
    const v = f ? entity[f.name] : undefined;
    return v == null || v === "" ? undefined : String(v);
  };
  const titleText = roleValue("title") ?? (entity[displayField] != null ? String(entity[displayField]) : entityId);
  const subtitleText = roleValue("subtitle");
  const badges = def.fields
    .filter((f) => f.display_role === "badge")
    .map((f) => entity[f.name])
    .filter((v) => v != null && v !== "")
    .map(String);

  const transitions = (entity._transitions ?? []) as AvailableTransition[];
  const workflowField = def.workflow?.field ?? "status";
  const currentState = entity[workflowField] != null ? String(entity[workflowField]) : "";

  const header = (
    <div style={headerStyle}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, minWidth: 0, flex: 1 }}>
        <NodeAvatar type="row" size="lg" name={titleText} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={titleStyle} title={titleText}>{titleText}</div>
          {subtitleText && <div style={subtitleStyle} title={subtitleText}>{subtitleText}</div>}
          {badges.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
              {badges.map((b, i) => <Pill key={i} variant="gray">{b}</Pill>)}
            </div>
          )}
          <div style={idStyle}>{entityId}</div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
        {mode === "panel" && (
          <button
            onClick={() => {
              // Sprint 12b — close the slide-in before navigating so the user
              // doesn't land on the full page with the panel still overlaying it.
              closePanel();
              navigate(`/entities/${entityTypeSlug}/${entityId}`);
            }}
            title="Open as full page"
            style={iconButtonStyle}
          >
            <i className="ti ti-arrow-up-right" style={{ fontSize: 16 }} aria-hidden="true" />
          </button>
        )}
        {mode === "panel" && onClose && (
          <button onClick={onClose} title="Close" aria-label="Close" style={iconButtonStyle}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );

  const stepper = def.workflow ? (
    <Block>
      <StageStepper
        workflow={def.workflow}
        currentState={currentState}
        transitions={transitions}
        onTransition={handleTransition}
        disabled={updateEntity.isPending}
      />
    </Block>
  ) : null;

  const fields = (
    <FieldList
      fields={def.fields}
      values={entity as Record<string, unknown>}
      forwardRefs={detail.data.forward_refs}
      onSave={handleFieldSave}
      onOpenRef={onOpenRef}
    />
  );

  const filesBlock = (
    <Block title="Files & folders">
      <RowFilesSection
        entityTypeSlug={entityTypeSlug}
        entityId={entityId}
        files={detail.data.owned.files}
        folders={detail.data.owned.folders}
      />
    </Block>
  );

  // Sprint 14: Activity section — mounted only when the type opts into
  // interaction history (the `activities` key is present in the payload).
  // Presence check, NOT truthy: an enabled type with no logged activity has
  // `activities.items === []` and should still render the section (so users can
  // log the first one).
  const activitiesBlock = "activities" in detail.data && detail.data.activities ? (
    <Block title="Activity">
      <RowActivitySection
        entityTypeSlug={entityTypeSlug}
        entityId={entityId}
        slice={detail.data.activities}
      />
    </Block>
  ) : null;

  // Sprint 26.2 — the shared cross-tag Related surface. Seeds with the grouped
  // regarding back-refs already in hand (get_row_detail), then layers the
  // find_related mention graph (cross-agent connections) beneath them.
  const backRefsBlock = (
    <Block title="Related">
      <RelatedSection
        subjectKind="entity"
        subjectId={entityId}
        seedBackRefs={detail.data.back_refs}
        onOpenRef={onOpenRef}
        title=""
      />
    </Block>
  );

  // Page mode at desktop width: two columns. Panel mode + narrow: single column.
  if (mode === "page" && wide) {
    return (
      <Frame mode={mode}>
        {showBack && onBack && <BackBar onBack={onBack} />}
        {header}
        {stepper}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)", gap: 24, marginTop: 8 }}>
          <div><Block title="Details">{fields}</Block></div>
          <div>{filesBlock}{activitiesBlock}{backRefsBlock}</div>
        </div>
      </Frame>
    );
  }

  return (
    <Frame mode={mode}>
      {showBack && onBack && <BackBar onBack={onBack} />}
      {header}
      {stepper}
      <Block title="Details">{fields}</Block>
      {filesBlock}
      {activitiesBlock}
      {backRefsBlock}
    </Frame>
  );
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

function Frame({ mode, children }: { mode: "panel" | "page"; children: ReactNode }) {
  const style: CSSProperties =
    mode === "page"
      ? { maxWidth: 1100, margin: "0 auto", fontFamily: "var(--font-sans)" }
      : { fontFamily: "var(--font-sans)" };
  return <div style={style}>{children}</div>;
}

function Block({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div style={{ padding: "14px 0", borderTop: title ? "var(--kuvio-border-default)" : "none" }}>
      {title && <div style={blockTitleStyle}>{title}</div>}
      {children}
    </div>
  );
}

function BackBar({ onBack }: { onBack: () => void }) {
  return (
    <button onClick={onBack} style={backBarStyle}>
      <i className="ti ti-arrow-left" style={{ fontSize: 14 }} aria-hidden="true" />
      Back
    </button>
  );
}

function DetailSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 8 }}>
      <div className="kuvio-skeleton" style={{ height: 44, borderRadius: "var(--border-radius-md)", background: "var(--color-background-secondary)" }} />
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="kuvio-skeleton" style={{ height: 28, borderRadius: "var(--border-radius-sm)", background: "var(--color-background-secondary)" }} />
      ))}
    </div>
  );
}

function useIsWide(): boolean {
  const [wide, setWide] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 900 : true));
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return wide;
}

function mutationMessage(err: unknown): string {
  if (err && typeof err === "object" && "message" in err) {
    return String((err as MutationError).message);
  }
  return "Couldn't save. Please try again.";
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const headerStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 8,
  paddingBottom: 8,
};

const titleStyle: CSSProperties = {
  fontSize: 18,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  lineHeight: 1.3,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const subtitleStyle: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-secondary)",
  marginTop: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const idStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  fontFamily: "var(--font-mono)",
  marginTop: 4,
};

const blockTitleStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: 8,
};

const iconButtonStyle: CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  padding: 4,
  display: "flex",
  borderRadius: "var(--border-radius-sm)",
};

const backBarStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "var(--color-text-secondary)",
  fontSize: 12,
  fontWeight: 500,
  fontFamily: "var(--font-sans)",
  padding: "0 0 8px",
};

const linkButtonStyle: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-info)",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
